"use client"

import { getStringValue } from "@/lib/string-helper"
import { toImageSrc } from "@/lib/utils"
import { TopSectionProps } from "@/types/types"
import Image from "next/image"

export const TopSection = ({ image, items, descriptions, className = "" }: TopSectionProps) => {
  const imageUrl = image ? toImageSrc(image) : null

  return (
    <div className={`flex flex-col sm:flex-row gap-[1.5625rem] sm:gap-[4.375rem] ${className}`}>
      {/* Left Side - Image (Desktop only) */}
      {imageUrl && (
        <div className="hidden sm:flex w-full sm:w-auto flex-shrink-0 justify-center sm:justify-start">
          <div className="relative w-full max-w-[600px] h-[300px] sm:w-[600px] sm:h-[489.5px] rounded-lg overflow-hidden">
            <Image
              src={imageUrl}
              alt="Top Section"
              fill
              className="object-cover"
              sizes="(max-width: 640px) 100vw, 507.5px"
            />
          </div>
        </div>
      )}

      {/* Right Side - Text Content */}
      <div className="flex-1 flex flex-col justify-center">
        {/* List Items */}
        {items && items.length > 0 && (
          <ul className="mb-1">
            {items.map((item, index) => {
              if (!item) return null
              const number = getStringValue(item.number)
              const text = getStringValue(item.text)
              if (!number && !text) return null
              return (
                <li key={index} className="text-[1.1875rem] text-[#333333] leading-[1.75rem] font-bold">
                  {number && <span>{number}</span>} {text && <span>{text}</span>}
                </li>
              )
            })}
          </ul>
        )}

        {/* Subtitle and Descriptions */}
        {descriptions && descriptions.length > 0 && (
          <div>
            {descriptions.map((description, index) => {
              if (!description) return null
              const text = getStringValue(description)
              if (!text) return null
              // First item is subtitle (font-bold), rest are descriptions (font-normal with margin-top on desktop)
              if (index === 0) {
                return (
                  <h3
                    key={index}
                    className="text-[1.1875rem] text-[#333333] sm:leading-[1.75rem] leading-[1.25rem] font-bold mb-0"
                  >
                    {text}
                  </h3>
                )
              }
              return (
                <p key={index} className="text-base text-[#333333] sm:leading-[1.75rem] leading-[1.25rem] font-normal">
                  {text}
                </p>
              )
            })}
          </div>
        )}
      </div>

      {/* Image - Mobile only (full width below text) */}
      {imageUrl && (
        <div className="block sm:hidden w-full">
          <div className="relative w-full h-[300px] rounded-lg overflow-hidden">
            <Image src={imageUrl} alt="Top Section" fill className="object-cover" sizes="100vw" />
          </div>
        </div>
      )}
    </div>
  )
}
